import { Controller } from '@hotwired/stimulus'
import Choices from 'choices.js'

// タグ入力コントローラー
// data-controller は wrapper div に付ける。Choices.js は input target に適用する。
// （input 要素に data-controller を付けると Choices.js の DOM 移動で
//   Stimulus が disconnect/connect を繰り返す無限ループになるため）
//
// 使用例:
//   <div data-controller="tag-input"
//        data-tag-input-field-name-value="post_setting[area_keywords][]"
//        data-tag-input-initial-tags-value="<%= tags.to_json %>"
//        data-tag-input-max-item-count-value="10">
//     <input type="text" data-tag-input-target="input">
//   </div>
export default class extends Controller {
  static targets = ['input']
  static values = {
    fieldName: String,
    initialTags: Array,
    maxItemCount: { type: Number, default: 10 }
  }

  declare readonly inputTarget: HTMLInputElement
  declare fieldNameValue: string
  declare initialTagsValue: string[]
  declare maxItemCountValue: number

  private choices!: Choices

  connect(): void {
    this.choices = new Choices(this.inputTarget, {
      removeItemButton: true,
      addItems: true,
      duplicateItemsAllowed: false,
      maxItemCount: this.maxItemCountValue,
      maxItemText: (count: number) => `最大${count}件まで追加できます`,
      items: this.initialTagsValue.map((tag: string) => ({ value: tag, label: tag })),
      addItemText: (value: string) => `Enter で「${value}」を追加`,
      noChoicesText: '',
      itemSelectText: '',
      allowHTML: false
    })

    this.inputTarget.addEventListener('addItem', this.handleAddItem)
    this.inputTarget.addEventListener('removeItem', this.handleRemoveItem)

    // サーバー側で描画された初期タグの hidden input をいったん除去してから JS 側で再生成する
    // （data-tag-value 属性の統一と重複送信防止のため）
    this.removeAllHiddenInputs()
    this.initialTagsValue.forEach((tag: string) => this.addHiddenInput(tag))
  }

  disconnect(): void {
    this.inputTarget.removeEventListener('addItem', this.handleAddItem)
    this.inputTarget.removeEventListener('removeItem', this.handleRemoveItem)
    this.removeAllHiddenInputs()
    this.choices.destroy()
  }

  private handleAddItem = (event: Event): void => {
    const e = event as CustomEvent
    this.addHiddenInput(e.detail.value as string)
  }

  private handleRemoveItem = (event: Event): void => {
    const e = event as CustomEvent
    this.removeHiddenInput(e.detail.value as string)
  }

  private addHiddenInput(value: string): void {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = this.fieldNameValue
    input.value = value
    input.dataset.tagValue = value
    this.element.appendChild(input)
  }

  private removeHiddenInput(value: string): void {
    const selector = `input[type="hidden"][data-tag-value="${CSS.escape(value)}"]`
    this.element.querySelectorAll(selector).forEach(el => el.remove())
  }

  private removeAllHiddenInputs(): void {
    this.element.querySelectorAll('input[type="hidden"][data-tag-value]').forEach(el => el.remove())
  }
}
